// ============================================================================
// OrgMobileList — vue liste/accordeon mobile (E1c NAV v2, item 10)
// ============================================================================
// Remplace l'arbre horizontal sous md : drill-in via fil d'Ariane, accordeon des
// reports directs, carte focus du noeud centre.

import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GS_COMPONENTS } from '@gs/ui';
import {
  directOrgReports,
  findOrgNode,
  type OrgNode,
} from '../../lib/org/hierarchy';
import { getInitials } from '../../lib/profile/initials';
import { useTranslation } from '../../i18n';

function PersonInitials({
  node,
  size = 'md',
}: {
  node: OrgNode;
  size?: 'md' | 'sm';
}) {
  const cls =
    size === 'sm'
      ? 'h-8 w-8 text-chip'
      : 'h-10 w-10 text-chip';
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200 ${cls} ${
        node.is_team_director ? 'ring-2 ring-brand-500/40' : ''
      }`}
    >
      {getInitials(node.full_name, node.email)}
    </span>
  );
}

export function OrgMobileList({
  roots,
  focusPath,
  highlightedEmail,
  expandedEmails,
  onDrillIn,
  onToggleExpand,
}: {
  roots: OrgNode[];
  focusPath: readonly string[];
  highlightedEmail?: string | null;
  expandedEmails: ReadonlySet<string>;
  onDrillIn: (email: string) => void;
  onToggleExpand: (email: string) => void;
}) {
  const { t } = useTranslation();

  const focusedEmail = focusPath.length > 0 ? focusPath[focusPath.length - 1] : null;
  const focusedNode = focusedEmail ? findOrgNode(roots, focusedEmail) : null;
  const listItems = focusedNode ? directOrgReports(focusedNode) : roots;

  if (listItems.length === 0 && !focusedNode) {
    return null;
  }

  return (
    <div className="space-y-3 md:hidden">
      {focusedNode ? (
        <div
          className="flex items-center gap-3 rounded-xl border border-brand-500 bg-white p-3.5 shadow-sm dark:bg-slate-800"
          role="status"
          aria-live="polite"
        >
          <PersonInitials node={focusedNode} />
          <div className="min-w-0 flex-1">
            {focusedNode.href ? (
              <Link
                to={focusedNode.href}
                className="block truncate text-body font-semibold text-slate-900 underline-offset-2 hover:underline dark:text-slate-100"
              >
                {focusedNode.full_name ?? focusedNode.email}
              </Link>
            ) : (
              <p className="truncate text-body font-semibold text-slate-900 dark:text-slate-100">
                {focusedNode.full_name ?? focusedNode.email}
              </p>
            )}
            {focusedNode.job_title ? (
              <p className="truncate text-meta text-slate-500 dark:text-slate-400">
                {focusedNode.job_title}
              </p>
            ) : null}
            {focusedNode.team_name ? (
              <p className="truncate text-chip text-slate-400 dark:text-slate-500">
                {focusedNode.team_name}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {listItems.length === 0 ? (
        <p className={`${GS_COMPONENTS.card} p-4 text-field text-slate-500 dark:text-slate-400`}>
          {t('gest:organigramme.mobile.noReports')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {listItems.map((node) => {
            const reports = directOrgReports(node);
            const hasReports = reports.length > 0;
            const isOpen = expandedEmails.has(node.email);
            const isHighlighted = highlightedEmail === node.email;

            return (
              <li
                key={node.email}
                className={`overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-slate-800 ${
                  isHighlighted ? 'border-brand-500 ring-1 ring-brand-500' : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <button
                  type="button"
                  className="flex w-full touch-manipulation items-center gap-2.5 px-3.5 py-3 text-left"
                  aria-expanded={hasReports ? isOpen : undefined}
                  onClick={() => {
                    if (hasReports) onToggleExpand(node.email);
                    else onDrillIn(node.email);
                  }}
                >
                  <PersonInitials node={node} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-field font-semibold text-slate-900 dark:text-slate-100">
                      {node.full_name ?? node.email}
                    </span>
                    <span className="block truncate text-meta text-slate-500 dark:text-slate-400">
                      {[node.job_title, node.team_name].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {hasReports ? (
                    <>
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-chip tabular-nums text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                        {t('gest:organigramme.mobile.reportsCount', { count: reports.length })}
                      </span>
                      <ChevronDown
                        size={18}
                        aria-hidden="true"
                        className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </>
                  ) : null}
                </button>
                {hasReports && isOpen ? (
                  <ul className="border-t border-slate-100 px-3 py-2 dark:border-slate-700">
                    {reports.map((child) => (
                      <li key={child.email}>
                        <button
                          type="button"
                          onClick={() => onDrillIn(child.email)}
                          className="flex w-full touch-manipulation items-center gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50"
                        >
                          <PersonInitials node={child} size="sm" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-field font-medium text-slate-800 dark:text-slate-100">
                              {child.full_name ?? child.email}
                            </span>
                            <span className="block truncate text-chip text-slate-400">
                              {child.job_title ?? child.email}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
